import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { ReleasingChargeRow } from '@/components/payments/releasing-charge-row';
import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import type { PaymentScheduleDay } from '@/types/payment-reporting';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';

/**
 * Um dia da agenda "Quando libera": cabeçalho com o dia e o total; ao tocar,
 * abre as cobranças que somam esse total (skill `collapse-reveal`: altura
 * medida animada nos dois sentidos, cabeçalho fora da animação).
 */
export function ScheduleDayRow({
  day,
  expanded,
  onToggle,
}: {
  day: PaymentScheduleDay;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [contentHeight, setContentHeight] = useState(0);
  const progress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [expanded, progress]);

  const collapseStyle = useAnimatedStyle(() => ({
    height: progress.value * contentHeight,
    opacity: progress.value,
  }));

  // API antiga (antes de `charges` existir) manda só data e total.
  const charges = day.charges ?? [];
  const count = charges.length;

  return (
    <View>
      <PressableScale
        haptic
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${formatDate(day.date, "EEEE, dd 'de' MMMM")}: ${formatCurrency(day.netAmount)}`}
        className="flex-row items-center gap-2 py-1"
        testID={`schedule-day-${day.date}`}
      >
        <View className="min-w-0 flex-1">
          <ThemedText className="text-sm text-neutral-700">
            {formatDate(day.date, "EEE, dd 'de' MMM")}
          </ThemedText>
          {count > 0 ? (
            <ThemedText className="text-xs text-neutral-400">
              {count === 1 ? '1 pagamento' : `${count} pagamentos`}
            </ThemedText>
          ) : null}
        </View>
        <ThemedText
          className="font-semibold text-sm text-neutral-900"
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {formatCurrency(day.netAmount)}
        </ThemedText>
        <Ionicons className="text-neutral-400" name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
      </PressableScale>

      <AnimatedView style={collapseStyle} className="overflow-hidden">
        <View
          style={{ position: 'absolute', left: 0, right: 0 }}
          onLayout={(e) => {
            const h = e.nativeEvent.layout.height;
            if (h > 0 && Math.abs(h - contentHeight) > 0.5) setContentHeight(h);
          }}
        >
          <View className="mt-1 rounded-xl bg-neutral-50 px-3">
            {charges.map((charge, index) => (
              <ReleasingChargeRow
                key={charge.id}
                charge={charge}
                isLast={index === charges.length - 1}
              />
            ))}
          </View>
        </View>
      </AnimatedView>
    </View>
  );
}
